import React, { useCallback, useEffect, useRef, useState } from "react";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { FaTrash } from "react-icons/fa";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import {
  deleteFile,
  getFilesByLead,
  uploadFile,
} from "../../../store/slices/FileDataSlice";
import {
  filterFilesForOperationsAttachmentView,
  isAdminAuthLevel,
  isOperationsLeadAttachmentUser,
  isOpsPurchaseOrderOrContract,
} from "../../../utils/opsLeadAttachments";

const FileAttachmentPopup = ({
  leads,
  row,
  selectedAction,
  popupProject,
  setPopupProject,
  employees,
  isViewOnly = false,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [id, setID] = useState("");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [SelectedCategory, setSelectedCategory] = useState("");
  const dropdownRefProbability = useRef(null);
  const [editfetchedfiles, seteditFetchedFiles] = useState({ payload: [] });
  const [fileNames, setFileNames] = useState([]);
  // const { leads } = useSelector((state) => state.leads);
  const auth = localStorage.getItem("auth");
  const Roles = localStorage.getItem("Roles");
  const isAdmin = isAdminAuthLevel(auth);
  const canShowAttachmentControls = !isAdmin && !isViewOnly;
  const normalizedAuth = String(auth || "").trim().toUpperCase();
  const isAccountingUser = normalizedAuth === "L5";
  const normalizeCategory = (v) => String(v || "").trim().toLowerCase();

  useEffect(() => { }, [dispatch]);

  useEffect(() => {
    if (!row) return;

    const projQId = row?.quotationId ?? row?.QuotationId;
    const matchedLead =
      projQId && Array.isArray(leads) && leads.length
        ? leads.find((lead) => (lead?.quotationId ?? lead?.QuotationId) === projQId)
        : Array.isArray(leads) && leads.length
          ? leads.find((lead) => lead?.ProjectName === row?.ProjectName)
          : null;

    // Hard resolve leadId even when `leads` list is missing/incomplete.
    const nextId =
      matchedLead?._id ??
      row?._id ??
      row?.id ??
      row?.leadId ??
      "";

    setID(nextId ? String(nextId) : "");
  }, [row, setID, leads]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefProbability.current &&
        !dropdownRefProbability.current.contains(event.target)
      ) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchFiles = useCallback(async () => {
    if (isAdmin) return;
    if (!id) {
      seteditFetchedFiles({ payload: [] });
      return;
    }
    try {
      const res = await dispatch(getFilesByLead(id)).unwrap();
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.payload)
          ? res.payload
          : Array.isArray(res?.files)
            ? res.files
            : [];
      seteditFetchedFiles({ payload: list });
    } catch (err) {
      console.error("Error fetching files:", err);
    }
  }, [dispatch, id, isAdmin]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);



  const isPopupOpen = popupProject === row.ProjectName;


  const handleFileChange = (e) => {
    if (isViewOnly) return;
    const selectedFiles = Array.from(e.target.files);
    const allowedFormats = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!SelectedCategory) {
      enqueueSnackbar("Please select a category first", {
        variant: "error",
      });
      return;
    }

    setFileNames((prev) => {
      const existing = Array.isArray(prev) ? prev : [];
      const validFiles = [];

      selectedFiles.forEach((file) => {
        if (!allowedFormats.includes(file.type)) {
          enqueueSnackbar(
            `Only PDF, DOC, DOCX, XLS, and XLSX files are allowed (${file.name})`,
            {
              variant: "error",
            }
          );
          return;
        }
        const fileWithCategory = new File([file], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });
        fileWithCategory.selectedCategory = SelectedCategory;
        validFiles.push(fileWithCategory);
      });

      if (validFiles.length === 0) return prev;

      seteditFetchedFiles((prevFetched) => {
        const currentFiles = prevFetched?.payload || [];

        const newFiles = validFiles.map((file) => ({
          id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          filepath: `uploads/filesdata/${file.name}`,
          filepathurl: URL.createObjectURL(file),
          leadId: id,
          selectedCategory: file.selectedCategory,
          isNew: true,
          name: file.name,
          type: file.type,
        }));

        handleFileUpload(id, newFiles);

        return {
          ...prevFetched,
          payload: [...currentFiles, ...newFiles],
        };
      });

      return [...existing, ...validFiles];
    });

    setSelectedCategory("");
    e.target.value = null;
  };

  const handleFileUpload = useCallback(
    async (id, newFiles) => {
      if (!newFiles || newFiles.length === 0) return;

      const uploadDate = new Date().toISOString().slice(0, 10);
      const formData = new FormData();
      formData.append("leadId", id);
      formData.append("uploadDate", uploadDate);

      try {
        // For each file, we need to fetch the actual file data from the blob URL
        for (const fileObj of newFiles) {
          // Fetch the file data from the blob URL
          const response = await fetch(fileObj.filepathurl);
          const blob = await response.blob();

          // Create a new File object from the blob
          const file = new File([blob], fileObj.name, { type: fileObj.type });

          // Append to FormData
          formData.append("selectedCategory", fileObj.selectedCategory);
          formData.append("files", file);
        }

        await dispatch(uploadFile(formData)).unwrap();
        enqueueSnackbar("File uploaded successfully", { variant: "success" });
      } catch (error) {
        enqueueSnackbar("Error occurred while uploading file", {
          variant: "error",
        });
        console.error(
          "Error uploading file:",
          error.response?.data?.error || error.message
        );
      } finally {
        fetchFiles();

      }
    },
    [dispatch, enqueueSnackbar, fetchFiles]
  );

  const handleFileDelete = async (file) => {
    if (isViewOnly) return;
    const email = localStorage.getItem("email");

    const id = file.id;
    const employee = employees.find((emp) => emp.EmpEmail === email);
    if (employee._id !== file.createdBy) {
      enqueueSnackbar("You are not allowed to deleted file", {
        variant: "error",
      });
      return;
    }
    try {
      await dispatch(deleteFile(id)).unwrap();

      // Update state by removing the deleted file
      seteditFetchedFiles((prev) => ({
        ...prev,
        payload: prev.payload.filter((f) => f.id !== id),
      }));

      enqueueSnackbar("File deleted successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Error occurred while deleting file", {
        variant: "error",
      });
      console.error("Error deleting file:", error);
    }
  };
  const handleFileDownload = (fileData) => {
    if (isViewOnly) return;
    try {
      if (!fileData || !fileData.filepathurl) {
        throw new Error("Invalid file data");
      }

      const fileUrl = fileData.filepathurl;
      const fileName = fileData.filename || fileUrl.split("/").pop();

      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", fileName);
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      enqueueSnackbar("File download started", { variant: "success" });
    } catch (error) {
      console.error("Download failed:", error);
      enqueueSnackbar("Failed to download file", { variant: "error" });
    }
  };

  const filesToDisplay = editfetchedfiles?.payload || [];
  const isOpsAttachmentUser = isOperationsLeadAttachmentUser(auth, Roles);
  const attachmentTableRows = isOpsAttachmentUser
    ? filterFilesForOperationsAttachmentView(filesToDisplay)
    : isAccountingUser
      ? filesToDisplay.filter((file) => {
          const cat = normalizeCategory(file.category || file.selectedCategory);
          return cat === "purchase order" || cat === "contract";
        })
      : filesToDisplay.filter((file) => {
          const cat = file.category || file.selectedCategory;
          return cat !== "Quote" && cat !== "Purchase Order";
        });

  return (
    <div
      className={`${selectedAction === "Forward" ? "w-[14%]" : "w-[15.5%]"
        } max-w-[15.6%]  flex flex-col p-1 align-top items-center overflow-hidden`}
    >
      <AttachFileIcon
        onClick={() => {
          if (row.ProjectManagerAction !== "rejected") {
            setPopupProject(row.ProjectName);
          }
        }}
        className={`cursor-pointer rotate-12 ${row.ProjectManagerAction === "rejected"
          ? "text-gray-400 cursor-not-allowed"
          : ""
          }`}
      />

      {isPopupOpen && (
        <div className="fixed w-full inset-0 bg-black/50 flex items-center justify-center z-[3500]">
          <div className="w-[50%] bg-white shadow-lg rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <div className="flex align-middle items-center gap-2">
                <h2 className="text-lg font-bold">Attachments </h2>
                <p>({"Project Name : "}{row.ProjectName})</p>
              </div>
              <button
                className="text-gray-500 hover:text-black"
                onClick={() => setPopupProject(null)}
              >
                Close
              </button>
            </div>
            {canShowAttachmentControls && (
            <div className="flex gap-2">
              <div className="relative bg-white">
                <div
                  ref={dropdownRefProbability}
                  className=" flex  p-0  rounded-xl align-middle text-[12px]"
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (isViewOnly) return;
                      setShowCategoryDropdown(!showCategoryDropdown);
                    }}
                    disabled={isViewOnly}
                    className={`border relative w-24 h-8 border-gray-400 text-nowrap rounded-md p-2 shadow-md overflow-hidden bg-gray-200 outline-none flex items-center justify-between ${isViewOnly ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <p className="text-left text-xs truncate">
                      {SelectedCategory ? SelectedCategory : "Category"}
                    </p>
                    <ExpandMoreIcon
                      color="white"
                      className="cursor-pointer bg-gray-200 flex-shrink-0"
                    />
                  </button>

                  {showCategoryDropdown && (
                    <div
                      style={{ zIndex: 9999 }}
                      className="absolute top-full left-0 w-40 p-1 cursor-pointer max-h-96 overflow-y-auto bg-white text-sm border border-gray-300 shadow-lg flex flex-col gap-1"
                    >
                      {(auth === "L3" || auth === "L2") && (
                        <>
                          <div
                            className="p-1 hover:bg-gray-200 cursor-pointer"
                            onClick={() => {
                              setSelectedCategory("Quote");
                              setShowCategoryDropdown(!showCategoryDropdown);
                            }}
                          >
                            Quote
                          </div>
                          <div
                            className="p-1 hover:bg-gray-200 cursor-pointer"
                            onClick={() => {
                              setSelectedCategory("Purchase Order");
                              setShowCategoryDropdown(!showCategoryDropdown);
                            }}
                          >
                            Purchase Order
                          </div>
                        </>
                      )}
                      {!isOpsAttachmentUser && (
                        <div
                          className="p-1 hover:bg-gray-200 cursor-pointer"
                          onClick={() => {
                            setSelectedCategory("Contract");
                            setShowCategoryDropdown(!showCategoryDropdown);
                          }}
                        >
                          Contract
                        </div>
                      )}
                      <div
                        className="p-1 hover:bg-gray-200 cursor-pointer"
                        onClick={() => {
                          setSelectedCategory("Communication");
                          setShowCategoryDropdown(!showCategoryDropdown);
                        }}
                      >
                        Communication
                      </div>
                      <div
                        className="p-1 hover:bg-gray-200 cursor-pointer"
                        onClick={() => {
                          setSelectedCategory("Miscellaneous");
                          setShowCategoryDropdown(!showCategoryDropdown);
                        }}
                      >
                        Miscellaneous
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <label
                  className={`text-xs p-2 w-24 h-8 text-center rounded-md flex items-center justify-center ${isViewOnly
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed opacity-50 pointer-events-none"
                    : !SelectedCategory
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-gray-800 text-white hover:text-black hover:bg-gray-300 cursor-pointer"
                    }`}
                >
                  <span>Upload File</span>
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFileChange(e)}
                    disabled={!SelectedCategory || isViewOnly}
                  />
                </label>

                <p className="truncate text-xs pl-1">
                  {fileNames?.name || "No file selected"}
                </p>
                <div className="flex gap-2 text-red-500 text-[12px] pl-3">
                  <p> only .pdf, .xlsx and .docx allowed</p>
                </div>
              </div>
            </div>
            )}

            <div className="w-full mt-4 text-left border-collapse text-[13px]">
              <div className="w-full ">
                <div className="border-b w-full flex">
                  <div className="py-2 px-4  w-[15%]">Sr. No.</div>
                  <div className="py-2 px-4  w-[40%]">File Name</div>
                  <div className="py-2 px-4  w-[15%]">Category</div>
                  <div className="py-2 px-4  text-center w-[15%]">Download</div>
                  <div className="py-2 px-4  text-center w-[15%]">Delete</div>
                </div>
              </div>
              <div className="w-full">
                {isAdmin ? (
                  <div className="py-4 px-4 text-center text-gray-500">
                    No attachments available.
                  </div>
                ) : (
                  attachmentTableRows.map((file, index) => {
                    const isRestricted =
                      isOpsAttachmentUser && isOpsPurchaseOrderOrContract(file);
                    return (
                      <div
                        className={`border-b w-full flex ${isRestricted ? "bg-gray-50 text-gray-400" : ""}`}
                        key={file.id ?? index}
                      >
                        <div className="py-2 px-4 w-[15%]">{index + 1}</div>
                        <div className="py-2 px-4  w-[40%] truncate overflow-hidden ">
                          {file.filename || file.name}
                        </div>
                        <div className="py-2 px-4  w-[15%] text-center truncate">
                          {file.category || file.selectedCategory}
                        </div>
                        <div className="py-2 px-4 text-center w-[15%]">
                          {isRestricted ? (
                            <span
                              className="inline-flex items-center justify-center rounded px-2 py-1 bg-emerald-600 text-white shadow-sm pointer-events-none select-none"
                              title="Download not available for this category"
                              aria-hidden
                            >
                              <CheckCircleIcon fontSize="small" />
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => { if (!isViewOnly) handleFileDownload(file); }}
                              disabled={isViewOnly}
                              className={`text-white px-2 py-1 rounded ${isViewOnly ? "bg-blue-500 opacity-50 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-700"}`}
                            >
                              <CloudDownloadIcon />
                            </button>
                          )}
                        </div>
                        <div className="py-2 px-4 text-center w-[15%]">
                          {isRestricted ? (
                            <span
                              className="inline-flex items-center justify-center rounded px-2 py-2 text-gray-300 cursor-default"
                              title="Delete not available for this category"
                              aria-hidden
                            >
                              <FaTrash size={18} />
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => { if (!isViewOnly) handleFileDelete(file); }}
                              disabled={isViewOnly}
                              className={`text-white px-2 py-2 rounded ${isViewOnly ? "bg-red-500 opacity-50 cursor-not-allowed" : "bg-red-500 hover:bg-red-700"}`}
                            >
                              <FaTrash size={18} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileAttachmentPopup;
